/*
 * Native Cognito USER_AUTH choice-based email-code sign-in (PRIMARY participant
 * auth). This is the flow mandated by PROGRAMME-DESIGN-AUTH-POPULATION-MODEL-V1
 * v3 (Duncan-LOCKED) + PRD-AUTH-POPULATION-IMPLEMENTATION-V1: email-code is the
 * PRIMARY sign-in for low-privilege participants; password is demoted to a
 * legacy/optional secondary path (src/auth/cognitoAuth.ts).
 *
 * WHY the native USER_AUTH flow (NOT a custom-auth Lambda emulation, NOT the
 * hosted UI): the WU-04 acceptance requires the flow be built on Cognito's
 * FIRST-CLASS choice-based USER_AUTH (InitiateAuth AuthFlow=USER_AUTH →
 * EMAIL_OTP challenge → RespondToAuthChallenge) so WU-05 passkey (WEB_AUTHN) is
 * PURELY ADDITIVE later — the same USER_AUTH entry point already advertises
 * WEB_AUTHN alongside EMAIL_OTP (ground-truthed live on the stage client
 * 3n3u9f96u5oj455k5niikej2og: AvailableChallenges = ["EMAIL_OTP","WEB_AUTHN"]).
 *
 * WHY direct cognito-idp JSON API over fetch (not amazon-cognito-identity-js):
 * the v6 library has no clean USER_AUTH/EMAIL_OTP surface; the repo already
 * speaks the raw JSON API for exactly this reason (cognitoAuth.globalSignOut,
 * cognitoProfile.ts). The browser calls https://cognito-idp.{region}.amazonaws
 * .com/ directly — it NEVER navigates to *.amazoncognito.com (no hosted UI).
 * The app client is a public SPA client (no client secret ⇒ no SECRET_HASH).
 *
 * The emailed code is the DELIBERATE security floor (Duncan build-caveat (c)):
 * control-of-mailbox is sufficient WHILE participants are low-privilege. EMAIL_OTP
 * additionally REQUIRES a verified email (email_verified=true) — see the
 * EmailUnverified surface below and the WU-04 email_verified audit.
 */
import { readAuthConfig } from './authConfig'
import { AuthFlowError, normaliseError, type ExactUsernameOpts } from './cognitoAuth'
import { probeEmailAuthoritative, type ProbeOutcome } from './emailProbe'
import { prepareUnconfirmedClaim } from './claimPrepare'
import { normalizeEmail } from './normalizeEmail'
import type { TokenSet } from './oidc'

const TARGET_INITIATE = 'AWSCognitoIdentityProviderService.InitiateAuth'
const TARGET_RESPOND = 'AWSCognitoIdentityProviderService.RespondToAuthChallenge'

/** Result of requesting an email code — carries the opaque Cognito Session that
 *  the subsequent RespondToAuthChallenge MUST echo back, plus the masked
 *  delivery destination (e.g. "j***@e***") Cognito returns for display. */
export interface EmailCodeChallenge {
  session: string
  /** Masked destination from ChallengeParameters.CODE_DELIVERY_DESTINATION. */
  destination?: string
}

interface CognitoApiError {
  __type?: string
  message?: string
  Message?: string
}

/** POST a JSON-1.1 cognito-idp action. On a non-2xx the body is a
 *  {__type, message} error object which we normalise through the SAME
 *  AuthFlowError surface the password path uses (cognitoAuth.normaliseError),
 *  so the UI keeps mapping ONE typed error set — never raw AWS names. */
async function callCognito(
  target: string,
  body: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<Record<string, unknown>> {
  const config = readAuthConfig()
  if (!config) throw new AuthFlowError('Unknown', 'auth not configured')
  let res: Response
  try {
    res = await fetchImpl(`https://cognito-idp.${config.region}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': target,
      },
      body: JSON.stringify(body),
    })
  } catch {
    // A thrown fetch is a transport failure (offline/DNS) — map to Network so
    // the UI shows the "check your connection" copy, never a dead spinner.
    throw new AuthFlowError('Network', 'Network problem — check your connection and try again.')
  }
  const text = await res.text().catch(() => '')
  const json = text ? (JSON.parse(text) as Record<string, unknown>) : {}
  if (!res.ok) {
    const err = json as CognitoApiError
    const rawType = (err.__type || '').split('#').pop() || ''
    const message = err.message || err.Message || 'Something went wrong. Please try again.'
    throw normaliseError({ name: rawType, message })
  }
  return json
}

/** The Cognito USERNAME on the wire. Default: canonical lowercase
 *  (normalizeEmail). `exactUsername` ⇒ a probe-RESOLVED, case-correct username
 *  used verbatim (NFC+trim only) — the case-sensitive pool holds legacy
 *  mixed-case usernames (Duncan checklist #3), and re-lowercasing one would
 *  target a different (or a NEW, duplicate) account. */
function wireUsername(email: string, opts: ExactUsernameOpts): string {
  return opts.exactUsername ? email.normalize('NFC').trim() : normalizeEmail(email)
}

function requireClientId(): string {
  const config = readAuthConfig()
  if (!config) throw new AuthFlowError('Unknown', 'auth not configured')
  return config.userPoolClientId
}

/**
 * Request an emailed sign-in code via the native USER_AUTH choice-based flow.
 *
 * InitiateAuth AuthFlow=USER_AUTH with PREFERRED_CHALLENGE=EMAIL_OTP asks
 * Cognito to go straight to the email code (skipping SELECT_CHALLENGE); the
 * branded email is composed + sent by the pool's already-live CustomEmailSender
 * (WU-01). The username is normalised (lowercased) at this boundary exactly like
 * every other outbound auth call (normalizeEmail) so the case-sensitive pool is
 * addressed consistently.
 *
 * ANTI-ENUMERATION: the pool has PreventUserExistenceErrors enabled, so an
 * UNKNOWN email returns a RANDOMISED SIMULATED challenge rather than an error:
 * sometimes SELECT_CHALLENGE without EMAIL_OTP, sometimes a FAKE EMAIL_OTP that
 * sends nothing (stage probes 2026-10-02). The answer is therefore ONLY
 * trustworthy for an account known to EXIST — the login UI calls this via
 * startEmailClaim AFTER SignUp returned UsernameExists (SignUp-first, #275
 * quality G1/G2), never directly for an unknown address.
 *
 * UNVERIFIED (build-caveat (c) / audit): EMAIL_OTP requires
 * email_verified=true, so no EMAIL_OTP is offered and we throw a DISTINCT
 * 'EmailUnverified' error; startEmailClaim then resends the sign-up code.
 */
export async function requestEmailCode(
  email: string,
  fetchImpl: typeof fetch = fetch,
  opts: ExactUsernameOpts = {},
): Promise<EmailCodeChallenge> {
  const username = wireUsername(email, opts)
  const first = await callCognito(TARGET_INITIATE, {
    AuthFlow: 'USER_AUTH',
    ClientId: requireClientId(),
    AuthParameters: { USERNAME: username, PREFERRED_CHALLENGE: 'EMAIL_OTP' },
  }, fetchImpl)

  const challengeName = first.ChallengeName as string | undefined
  const available = (first.AvailableChallenges as string[] | undefined) ?? []

  if (challengeName === 'EMAIL_OTP') {
    return { session: first.Session as string, destination: destinationOf(first) }
  }

  // Defensive: some pool configs answer USER_AUTH with SELECT_CHALLENGE first.
  // If EMAIL_OTP is available, ANSWER it to obtain the EMAIL_OTP challenge +
  // a fresh Session; if it is NOT available, an existing account is telling us
  // the email cannot receive an OTP (unverified) — surface that distinctly.
  if (challengeName === 'SELECT_CHALLENGE' || (challengeName == null && available.length)) {
    if (!available.includes('EMAIL_OTP')) {
      throw new AuthFlowError('EmailUnverified', 'This account cannot receive an email code yet — its email address is not verified.')
    }
    const selected = await callCognito(TARGET_RESPOND, {
      ChallengeName: 'SELECT_CHALLENGE',
      ClientId: requireClientId(),
      Session: first.Session as string,
      ChallengeResponses: { USERNAME: username, ANSWER: 'EMAIL_OTP' },
    }, fetchImpl)
    if ((selected.ChallengeName as string | undefined) !== 'EMAIL_OTP') {
      throw new AuthFlowError('Unknown', 'Could not start the email-code sign-in.')
    }
    return { session: selected.Session as string, destination: destinationOf(selected) }
  }

  throw new AuthFlowError('Unknown', 'Could not start the email-code sign-in.')
}

function destinationOf(challenge: Record<string, unknown>): string | undefined {
  const params = challenge.ChallengeParameters as Record<string, string> | undefined
  return params?.CODE_DELIVERY_DESTINATION
}

/**
 * Answer the EMAIL_OTP challenge with the user-entered code and receive tokens.
 * The Session from requestEmailCode MUST be echoed back verbatim. On success the
 * native AuthenticationResult maps 1:1 onto the existing TokenSet, so session.ts
 * stays the single session source of truth (the access token intrinsically
 * carries aws.cognito.signin.user.admin, so Profile GetUser/UpdateUserAttributes
 * work unchanged — same guarantee as the SRP path).
 *
 * Error surface (typed, never raw AWS names):
 *  - CodeMismatch  → the entered code is wrong (the same Session may be retried).
 *  - ExpiredCode / NotAuthorized → the code/session has lapsed; request a fresh
 *    code (the UI restarts the send).
 */
export async function submitEmailCode(
  email: string,
  code: string,
  session: string,
  fetchImpl: typeof fetch = fetch,
  opts: ExactUsernameOpts = {},
): Promise<TokenSet> {
  const username = wireUsername(email, opts)
  const res = await callCognito(TARGET_RESPOND, {
    ChallengeName: 'EMAIL_OTP',
    ClientId: requireClientId(),
    Session: session,
    ChallengeResponses: { USERNAME: username, EMAIL_OTP_CODE: code.trim() },
  }, fetchImpl)

  return tokensFrom(res)
}

/** Map a cognito-idp AuthenticationResult onto the shared TokenSet. Anything
 *  short of real tokens (e.g. a further MFA challenge — out of scope for the
 *  participant floor) throws: we never claim signed-in without tokens. */
function tokensFrom(res: Record<string, unknown>): TokenSet {
  const auth = res.AuthenticationResult as
    | { IdToken: string; AccessToken: string; RefreshToken?: string; ExpiresIn: number }
    | undefined
  if (!auth?.IdToken || !auth?.AccessToken) {
    throw new AuthFlowError('Unknown', 'Sign-in did not complete — please try again.')
  }
  return {
    idToken: auth.IdToken,
    accessToken: auth.AccessToken,
    refreshToken: auth.RefreshToken,
    expiresIn: auth.ExpiresIn,
  }
}

/** Length of the emailed USER_AUTH EMAIL_OTP sign-in code. Cognito sends 8
 *  digits (Duncan's prod test 2026-10-01) and its challenge response carries no
 *  length field, so this is the single named source the CODE step reads. NOT the
 *  sign-up confirmation / forgot-password codes, which Cognito sends as 6. */
export const EMAIL_OTP_CODE_LENGTH = 8

/** Length of the Cognito SIGN-UP verification code (ConfirmSignUp), which the
 *  claim-by-email-code path sends instead of an EMAIL_OTP. */
export const CLAIM_CODE_LENGTH = 6

/** The CODE step accepts any code between these lengths — the SAME bounds
 *  whichever code was sent, so the box (maxlength, enabled-at) never reveals
 *  whether the address had an account (non-enumerating claim, see
 *  startEmailClaim). A 6-digit entry against an 8-digit OTP is simply a
 *  CodeMismatch from Cognito. */
export const CODE_INPUT_MIN_LENGTH = CLAIM_CODE_LENGTH
export const CODE_INPUT_MAX_LENGTH = EMAIL_OTP_CODE_LENGTH

// ---------------------------------------------------------------------------
// CLAIM-BY-EMAIL-CODE (BOARDROOM-2026-10-02-090708)
// ---------------------------------------------------------------------------
//
// A guest buyer (purchasers are guest-first, PROGRAMME-DESIGN-AUTH-POPULATION-
// MODEL-V1 v3) has NO Cognito account, and an abandoned sign-up leaves an
// UNCONFIRMED one. Neither can receive an EMAIL_OTP (it requires a verified
// email). And InitiateAuth CANNOT tell an unknown address apart: Cognito's
// anti-enumeration answer for an UNKNOWN user is RANDOMISED — a simulated
// SELECT_CHALLENGE without EMAIL_OTP (stage ["WEB_AUTHN"], prod
// ["PASSWORD_SRP","PASSWORD"]) OR a FAKE EMAIL_OTP challenge that sends nothing
// (boardroom probe of 6 unknown addresses on stage, 2026-10-02: 2 of 6 fake
// OTPs). Asking InitiateAuth first stranded new buyers on a code screen with no
// email (quality stage leg on #275, G1/G2). And a blind SignUp is unsafe too:
// the pool is case-SENSITIVE and legacy usernames were minted as-typed, so a
// lowercase SignUp for a mixed-case legacy account mints a DUPLICATE (#275
// release blocker, PROGRAMME-RULE-NO-PARTIAL-SERVICE-TO-PRODUCTION-V1). So the
// AUTHORITATIVE my-api probe (AdminGetUser, case-resolving) decides FIRST; see
// startEmailClaim for the routing and the fail-closed rule.
//
// The claim step still delivers ONE code and finishes sign-in with NO password
// step: Cognito SignUp with a generated, never-shown password → Cognito emails
// the sign-up verification code → ConfirmSignUp (email_verified=true) → sign in
// via "sign in after sign-up" (ConfirmSignUp's Session → InitiateAuth USER_AUTH
// + Session, no second code), else an EMAIL_OTP fallback (the email is now
// verified, so the normal code flow works). Every call after the probe targets
// the probe-RESOLVED username verbatim (ClaimStart.username).
//
// A typist using SOMEONE ELSE's address only ever creates an UNCONFIRMED
// account whose code goes to the owner's mailbox; nothing signs in without it.
// An UNCONFIRMED account (an abandoned sign-up, or a repeat attempt) is never
// re-signed-up: my-api PREPARE (random permanent password ⇒ CONFIRMED, so a
// password set before the owner arrived stops working; email_verified=true)
// then ONE 8-digit EMAIL_OTP (#275 boardroom ruling A).

const TARGET_SIGN_UP = 'AWSCognitoIdentityProviderService.SignUp'
const TARGET_CONFIRM_SIGN_UP = 'AWSCognitoIdentityProviderService.ConfirmSignUp'
const TARGET_RESEND_CONFIRMATION = 'AWSCognitoIdentityProviderService.ResendConfirmationCode'

const PW_LOWER = 'abcdefghijkmnopqrstuvwxyz'
const PW_UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
const PW_DIGIT = '23456789'
const PW_SYMBOL = '!#%*+-=?@^_~'
const PW_ALL = PW_LOWER + PW_UPPER + PW_DIGIT + PW_SYMBOL

/** Unbiased crypto-random index in [0, n) (rejection sampling). */
function randomIndex(n: number): number {
  const limit = Math.floor(0x100000000 / n) * n
  const buf = new Uint32Array(1)
  for (;;) {
    crypto.getRandomValues(buf)
    if (buf[0] < limit) return buf[0] % n
  }
}

/**
 * A strong random password for the claim SignUp (40 chars, crypto-random, at
 * least one lower/upper/digit/symbol so any Cognito password policy passes).
 * It exists ONLY inside startEmailClaim's SignUp request: never returned to
 * the UI, never stored, never logged. The participant never needs it — email
 * code is their PRIMARY sign-in, and "Forgot your password?" sets a real one
 * if they ever want the legacy password path.
 */
export function generateClaimPassword(length = 40): string {
  const chars = [
    PW_LOWER[randomIndex(PW_LOWER.length)],
    PW_UPPER[randomIndex(PW_UPPER.length)],
    PW_DIGIT[randomIndex(PW_DIGIT.length)],
    PW_SYMBOL[randomIndex(PW_SYMBOL.length)],
  ]
  while (chars.length < length) chars.push(PW_ALL[randomIndex(PW_ALL.length)])
  // Fisher–Yates so the guaranteed classes are not always at the front.
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = randomIndex(i + 1)
    ;[chars[i], chars[j]] = [chars[j], chars[i]]
  }
  return chars.join('')
}

/** Outcome of starting a claim. `claim` ⇒ a 6-digit sign-up verification
 *  code was emailed to a NEW account (SignUp); `otp` ⇒ ONE 8-digit EMAIL_OTP
 *  was sent (verified account, a just-prepared unconfirmed one, or an
 *  auto-confirmed new one); `password-only` ⇒ an existing account no code can
 *  be emailed to — the caller routes to the password path. `username` is the
 *  EXACT Cognito username every later call (ConfirmSignUp, sign-in after
 *  sign-up, OTP fallback, resend, RespondToAuthChallenge) MUST target — the
 *  probe-resolved, case-correct one for an existing account. */
export type ClaimStart =
  | { kind: 'claim'; branch: 'created'; username: string; session?: string; destination?: string }
  | { kind: 'otp'; username: string; challenge: EmailCodeChallenge }
  | { kind: 'password-only' }

function deliveryDestination(res: Record<string, unknown>): string | undefined {
  const d = res.CodeDeliveryDetails as { Destination?: string } | undefined
  return d?.Destination
}

const EXACT: ExactUsernameOpts = { exactUsername: true }

/** True for the OTP refusals that mean "this account cannot take an
 *  EMAIL_OTP as it stands" (not verified / not confirmed). */
function otpNotAvailable(err: AuthFlowError): boolean {
  return err.code === 'EmailUnverified' || err.code === 'UserNotConfirmed'
}

/** An EXISTING account (probe-resolved `username`, used verbatim): the real
 *  EMAIL_OTP if it can take one; if it is unconfirmed/unverified, PREPARE it
 *  then OTP; a reset-required account → password-only. NEVER SignUp — on the
 *  case-sensitive pool a lowercase SignUp for a mixed-case legacy username
 *  would mint a DUPLICATE account. */
async function codeForExisting(
  email: string,
  username: string,
  fetchImpl: typeof fetch,
  prepare: (email: string) => Promise<void>,
): Promise<ClaimStart> {
  try {
    return { kind: 'otp', username, challenge: await requestEmailCode(username, fetchImpl, EXACT) }
  } catch (e) {
    const err = e instanceof AuthFlowError ? e : normaliseError(e)
    if (err.code === 'PasswordResetRequired') return { kind: 'password-only' }
    if (!otpNotAvailable(err)) throw err
  }
  return prepareThenOtp(email, username, fetchImpl, prepare)
}

/**
 * UNCONFIRMED branch (#275 boardroom ruling A): my-api PREPARE (random
 * permanent password ⇒ CONFIRMED, so a password set before the owner arrived
 * stops working; email_verified=true) THEN ONE EMAIL_OTP to the probe-resolved
 * username — the owner proves mailbox control by entering it. NO
 * ResendConfirmationCode and NO ConfirmSignUp on this branch: the OTP path
 * never confirms anything itself. A prepare failure throws BEFORE any Cognito
 * call (fail closed: no code is sent). If the OTP is still refused, prepare was
 * a no-op (a CONFIRMED account whose email is unverified) → password-only.
 */
async function prepareThenOtp(
  email: string,
  username: string,
  fetchImpl: typeof fetch,
  prepare: (email: string) => Promise<void>,
): Promise<ClaimStart> {
  await prepare(email)
  try {
    return { kind: 'otp', username, challenge: await requestEmailCode(username, fetchImpl, EXACT) }
  } catch (e) {
    const err = e instanceof AuthFlowError ? e : normaliseError(e)
    if (otpNotAvailable(err) || err.code === 'PasswordResetRequired') return { kind: 'password-only' }
    throw err
  }
}

/**
 * Send ONE code for any address, routed by the AUTHORITATIVE account probe
 * (my-api POST /account/probe → AdminGetUser lowercase → as-typed → ListUsers
 * email; immune to PreventUserExistenceErrors; resolves the case-correct
 * username) BEFORE any SignUp (#275: a blind lowercase SignUp duplicated
 * mixed-case legacy accounts on the case-sensitive pool):
 *   no-account                       → SignUp (canonical lowercase) → 6-digit
 *                                      sign-up code → ConfirmSignUp (Login)
 *   unconfirmed                      → NEVER SignUp: PREPARE → 8-digit EMAIL_OTP
 *   exists-confirmed / exists /
 *   reset-required                   → NEVER SignUp: EMAIL_OTP to probe.username;
 *                                      unconfirmed/unverified → PREPARE → OTP;
 *                                      reset-required refusal → password-only
 *   rate-limited (429)               → AuthFlowError('TooManyRequests')
 *   indeterminate / NOT authoritative (my-api unreachable → SRP fallback,
 *   which says 'exists' for ANY address) → FAIL CLOSED: throw (the UI shows
 *   its neutral "couldn't send a code" copy). Neither guess is safe: a blind
 *   SignUp can duplicate a legacy account, and a blind InitiateAuth for an
 *   unknown address gets Cognito's randomised FAKE EMAIL_OTP (#275 G1/G2).
 * InitiateAuth is therefore only ever asked about an account known to exist.
 * UsernameExists on the no-account SignUp (a race with another tab/device)
 * routes to the existing-account path, never surfaces.
 */
export async function startEmailClaim(
  email: string,
  fetchImpl: typeof fetch = fetch,
  probe: (email: string) => Promise<ProbeOutcome> = (e) => probeEmailAuthoritative(e),
  prepare: (email: string) => Promise<void> = (e) => prepareUnconfirmedClaim(e),
): Promise<ClaimStart> {
  let outcome: ProbeOutcome
  try {
    outcome = await probe(email)
  } catch {
    outcome = { result: 'indeterminate', username: normalizeEmail(email) }
  }
  if (outcome.rateLimited) throw new AuthFlowError('TooManyRequests', 'rate limited')
  if (!outcome.authoritative || outcome.result === 'indeterminate') {
    // Reason only — never the address (PII).
    console.warn(`[auth] claim-by-email-code: account probe not authoritative (result=${outcome.result}, authoritative=${outcome.authoritative === true}) — failing closed, no SignUp/InitiateAuth`)
    throw new AuthFlowError('Unknown', 'account probe unavailable')
  }
  switch (outcome.result) {
    case 'unconfirmed':
      return prepareThenOtp(email, outcome.username, fetchImpl, prepare)
    case 'exists-confirmed':
    case 'exists':
    case 'reset-required':
      return codeForExisting(email, outcome.username, fetchImpl, prepare)
    case 'no-account':
    default:
      break
  }
  // NO ACCOUNT (authoritatively): create it — canonical lowercase, always.
  const username = normalizeEmail(email)
  try {
    const res = await callCognito(TARGET_SIGN_UP, {
      ClientId: requireClientId(),
      Username: username,
      Password: generateClaimPassword(),
      // Email as username; the get-ready wizard collects name/profile later —
      // identical attribute set to cognitoAuth.signUp.
      UserAttributes: [{ Name: 'email', Value: username }],
    }, fetchImpl)
    if (res.UserConfirmed === true) {
      // Auto-confirmed pool (no code was sent) — the normal OTP flow applies.
      return { kind: 'otp', username, challenge: await requestEmailCode(username, fetchImpl, EXACT) }
    }
    return {
      kind: 'claim',
      branch: 'created',
      username,
      session: typeof res.Session === 'string' ? res.Session : undefined,
      destination: deliveryDestination(res),
    }
  } catch (e) {
    const err = e instanceof AuthFlowError ? e : normaliseError(e)
    if (err.code !== 'UsernameExists') throw err
  }
  // Created between the probe and SignUp (another tab/device): it exists now.
  return codeForExisting(email, username, fetchImpl, prepare)
}

/** Resend the claim (sign-up verification) code. Returns the masked
 *  destination when Cognito provides one. */
export async function resendClaimCode(
  email: string,
  fetchImpl: typeof fetch = fetch,
  opts: ExactUsernameOpts = {},
): Promise<string | undefined> {
  const res = await callCognito(TARGET_RESEND_CONFIRMATION, {
    ClientId: requireClientId(),
    Username: wireUsername(email, opts),
  }, fetchImpl)
  return deliveryDestination(res)
}

/**
 * ConfirmSignUp with the emailed claim code (sets email_verified=true). Passes
 * the SignUp Session when we hold one so Cognito returns a sign-in Session
 * ("sign in after sign-up"). Returns that Session if Cognito gave one.
 */
export async function confirmClaimCode(
  email: string,
  code: string,
  signUpSession?: string,
  fetchImpl: typeof fetch = fetch,
  opts: ExactUsernameOpts = {},
): Promise<{ session?: string }> {
  const body: Record<string, unknown> = {
    ClientId: requireClientId(),
    Username: wireUsername(email, opts),
    ConfirmationCode: code.trim(),
  }
  if (signUpSession) body.Session = signUpSession
  const res = await callCognito(TARGET_CONFIRM_SIGN_UP, body, fetchImpl)
  return { session: typeof res.Session === 'string' ? res.Session : undefined }
}

/** "Sign in after sign-up": InitiateAuth USER_AUTH with the ConfirmSignUp
 *  Session — tokens with NO second code. Throws if Cognito does not complete
 *  (the caller then falls back to an EMAIL_OTP). */
export async function signInAfterClaim(
  email: string,
  session: string,
  fetchImpl: typeof fetch = fetch,
  opts: ExactUsernameOpts = {},
): Promise<TokenSet> {
  const res = await callCognito(TARGET_INITIATE, {
    AuthFlow: 'USER_AUTH',
    ClientId: requireClientId(),
    AuthParameters: { USERNAME: wireUsername(email, opts) },
    Session: session,
  }, fetchImpl)
  return tokensFrom(res)
}

/** requestEmailCode is idempotent from the caller's view — a resend is simply a
 *  fresh InitiateAuth (a NEW code + NEW Session; the previous one is discarded).
 *  Named for call-site clarity. */
export const resendEmailCode = requestEmailCode

// ---------------------------------------------------------------------------
// Deep-link prefill (LOCKED canon behaviour: PRE-FILL, NEVER auto-submit)
// ---------------------------------------------------------------------------

export interface SignInDeepLink {
  /** The emailed sign-in code, URL-decoded, verbatim (length-agnostic). */
  code: string
  /** OPTIONAL opaque continuation hint (`u`) — captured verbatim if present,
   *  never required and never acted on here; absent/unknown is fine. */
  u?: string
}

// ---------------------------------------------------------------------------
// Pending-challenge persistence (deep-link + navigation survival)
// ---------------------------------------------------------------------------

/**
 * A Cognito EMAIL_OTP code can only be answered against the opaque `Session`
 * returned by the InitiateAuth that SENT it. A branded-email deep-link opened
 * in a fresh tab (common: request the code on a phone, tap the email button on
 * the same phone) would otherwise have no Session in memory. We persist the
 * pending {email, session} to localStorage (shared across same-origin tabs) so
 * the code-entry step — deep-linked or navigated — can complete the SAME
 * challenge that emailed the code, instead of silently re-issuing (which would
 * invalidate the code the user is holding). Cleared on success/failure; the
 * value is a short-lived opaque challenge token, never a credential.
 *
 * A deep-link opened on a DIFFERENT device has no persisted session — the
 * code-entry step then honestly offers "resend a fresh code" rather than failing
 * opaquely.
 */
const PENDING_KEY = 'mytrumin.emailcode.v1'

export interface PendingEmailCode {
  email: string
  /** EMAIL_OTP challenge Session, or the SignUp Session for a claim ('' when
   *  the claim has none — e.g. the unconfirmed-account resend). */
  session: string
  /** Which code the email carries: an EMAIL_OTP (default, absent on records
   *  written before the claim step) or a sign-up verification code. */
  kind?: 'otp' | 'claim'
  /** The EXACT Cognito username the code was sent to (probe-resolved; may be
   *  mixed-case on the case-sensitive pool). Absent on older records ⇒ the
   *  canonical lowercase form of `email`. */
  username?: string
  /** ms epoch when THIS browser sent the code (absent on records written
   *  before the reload guard) — freshPendingEmailCode's age check. */
  sentAt?: number
  /** true when the order-email ?email= link AUTO-sent this code: a bare reload
   *  (the param is stripped after the send) may resume it on the CODE step. */
  autoSent?: boolean
}

export function storePendingEmailCode(pending: PendingEmailCode): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(pending))
  } catch {
    // no-op: private-mode / storage-disabled degrades to in-memory session only
  }
}

export function loadPendingEmailCode(): PendingEmailCode | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY)
    if (!raw) return null
    const p = JSON.parse(raw) as PendingEmailCode
    if (!p || typeof p.email !== 'string' || typeof p.session !== 'string') return null
    const username = typeof p.username === 'string' && p.username ? { username: p.username } : {}
    const extra = {
      ...username,
      ...(typeof p.sentAt === 'number' && Number.isFinite(p.sentAt) ? { sentAt: p.sentAt } : {}),
      ...(p.autoSent === true ? { autoSent: true } : {}),
    }
    // A claim may legitimately carry no session; an OTP record never does.
    if (p.kind === 'claim') return { email: p.email, session: p.session, kind: 'claim', ...extra }
    return p.session ? { email: p.email, session: p.session, ...extra } : null
  } catch {
    return null
  }
}

/** How long a sent code is treated as still usable for a NO-RESEND resume
 *  (boardroom 2026-10-08, the ?email= reload / mail-scanner guard). OTP = the
 *  pool's auth_session_validity (3 min — the challenge session dies with it);
 *  a sign-up claim code lives far longer in Cognito, 10 min keeps the resume
 *  window short so a genuinely lost email is re-sent on the next arrival. */
export const PENDING_FRESH_MS = { otp: 3 * 60_000, claim: 10 * 60_000 } as const

/** The persisted pending code IFF it is fresh (sent by THIS browser within its
 *  window), optionally for a specific email (normalised compare) and/or only
 *  an order-email auto-send. Records without sentAt are never fresh. */
export function freshPendingEmailCode(
  opts: { email?: string; autoSentOnly?: boolean } = {},
  now: number = Date.now(),
): PendingEmailCode | null {
  const p = loadPendingEmailCode()
  if (!p || typeof p.sentAt !== 'number') return null
  if (opts.autoSentOnly && !p.autoSent) return null
  if (opts.email !== undefined && normalizeEmail(p.email) !== normalizeEmail(opts.email)) return null
  const age = now - p.sentAt
  const limit = p.kind === 'claim' ? PENDING_FRESH_MS.claim : PENDING_FRESH_MS.otp
  return age >= 0 && age < limit ? p : null
}

export function clearPendingEmailCode(): void {
  try {
    localStorage.removeItem(PENDING_KEY)
  } catch {
    // no-op
  }
}

/**
 * Parse the participant sign-in-code deep-link's query string into { code, u? }
 * for the code-entry step to PRE-FILL. PURE: returns data only, NEVER submits —
 * the "lands pre-filled and never auto-submits" canon (PROGRAMME-DESIGN-AUTH-
 * POPULATION-MODEL-V1 v3) is enforced by the caller, which sets state from this
 * and waits for an explicit user action.
 *
 * PINNED to the settings-side SINGLE-SOURCE contract (BOARDROOM-2026-09-19-
 * 211648: buildSignInCodeDeepLink / parseCodeFromSearch sharing
 * SIGN_IN_CODE_QUERY_KEY). The participant link is EXACTLY
 *     <env-origin>/sign-in?code=<url-encoded-code>[&u=<opaque continuation hint>]
 * (stage https://stage-my.trumin.com/sign-in?code=… ; prod https://my.trumin.com/…).
 * ONE code key: `code`. There is NO email param (and by construction no
 * submit/confirm/go flag) — anything else in the query is ignored (the older
 * tolerant `otp`/`sign_in_code`/`c` aliases + `?email=` handling are RETIRED).
 *
 * LENGTH-AGNOSTIC: the engine passes Cognito's code through verbatim (EMAIL_OTP
 * is 8 digits per Cognito, see EMAIL_OTP_CODE_LENGTH, but the link parser does
 * NOT hard-pin it). We pin on
 * key=code and tolerate whatever value it carries — with two exceptions that are
 * NOT usable codes and must NOT prefill garbage, so both land on the normal
 * request-a-code step instead:
 *   (1) an empty / whitespace-only value; and
 *   (2) a verbatim Cognito NATIVE-TEMPLATE placeholder token (e.g. `{####}`) —
 *       in native-template mode Cognito can preserve the `{####}` placeholder
 *       into the link un-substituted, and prefilling it would drop "{####}"
 *       into the code box (a real code never contains braces).
 *
 * Path=/sign-in is served by the SPA gate: for any UNAUTHED path the app renders
 * <Login/>, which reads window.location.search and calls this (see src/App.tsx);
 * the infra-owned R2/Cloudflare deploy already serves index.html for arbitrary
 * deep paths (/orders/x, /claim/:t, /auth/callback), so /sign-in never 404s.
 */
export function parseSignInDeepLink(search: string): SignInDeepLink | null {
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  } catch {
    return null
  }
  const code = (params.get('code') ?? '').trim() // URLSearchParams URL-decodes
  // Empty/whitespace, or an un-substituted `{…}` native-template placeholder ⇒
  // not a usable code (a real Cognito code never carries braces).
  if (!code || /[{}]/.test(code)) return null
  const u = (params.get('u') ?? '').trim()
  return u ? { code, u } : { code }
}
